import { Box, VStack, HStack, Text, Input, Button, Divider, useToast, Select, FormControl, FormLabel, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure, Checkbox, Link, InputGroup, InputRightElement, IconButton, useColorModeValue, SimpleGrid, Container } from '@chakra-ui/react';
import { FiMail, FiPhone, FiEye, FiEyeOff } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import IconWrapper from '../components/IconWrapper';
import { signupWithEmail, loginWithGoogle, initializeRecaptcha, sendPhoneVerification, verifyPhoneCode, signupWithPhone, sendEmailVerificationToUser, logout } from '../services/authService';
import { createUserProfile } from '../services/userService';
import { useState, useEffect } from 'react';

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen: isPhoneModalOpen, onOpen: onPhoneModalOpen, onClose: onPhoneModalClose } = useDisclosure();

  // Form fields
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [language, setLanguage] = useState('en');
  const [location, setLocation] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');
  const [backupEmail, setBackupEmail] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Phone verification
  const [verificationCode, setVerificationCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState<any>(null);
  const [isVerifyingPhone, setIsVerifyingPhone] = useState(false);
  
  // Password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.700', 'gray.300');
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const inputBorder = useColorModeValue('gray.200', 'gray.600');
  const inputFocusBg = useColorModeValue('white', 'gray.600');
  const inputFocusBorder = useColorModeValue('purple.500', 'purple.400');

  useEffect(() => {
    // Initialize reCAPTCHA
    initializeRecaptcha('recaptcha-container');
  }, []);

  const handleGoogleSignup = async () => {
    try {
      const user = await loginWithGoogle();
      await createUserProfile({ 
        uid: user.uid, 
        email: user.email || undefined, 
        name: user.displayName || undefined, 
        avatarUrl: user.photoURL || undefined,
        username: user.displayName?.toLowerCase().replace(/\s+/g, '') || undefined,
        language,
        location,
        age: age ? parseInt(age) : undefined,
        gender,
        backupEmail: backupEmail || undefined,
        isEmailVerified: user.emailVerified
      });
      
      // Send email verification
      await sendEmailVerificationToUser();
      
      // Log out user immediately - they must verify email before logging in
      await logout();
      
      toast({ 
        title: 'Account created!', 
        description: 'Please check your email to verify your account. You must verify your email before you can sign in.', 
        status: 'success',
        duration: 8000,
        isClosable: true
      });
      
      // Redirect to login page instead of home
      navigate('/login');
    } catch (e: any) {
      toast({ title: 'Google signup failed', description: e.message, status: 'error' });
    }
  };

  const handleEmailSignup = async () => {
    if (!acceptedTerms) {
      toast({
        title: 'Terms and Conditions Required',
        description: 'Please accept the Terms and Conditions to create an account.',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }
    
    if (password !== confirmPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'Please make sure both password fields match.',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }
    
    if (password.length < 6) {
      toast({
        title: 'Password too short',
        description: 'Password must be at least 6 characters long.',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    try {
      const user = await signupWithEmail(email, password, name);
      
      await createUserProfile({ 
        uid: user.uid, 
        username, 
        name, 
        email,
        phoneNumber: phone || undefined,
        language,
        location,
        age: age ? parseInt(age) : undefined,
        gender,
        backupEmail: backupEmail || undefined,
        isEmailVerified: user.emailVerified
      });
      
      // Send email verification
      await sendEmailVerificationToUser();
      
      // Log out user immediately - they must verify email before logging in
      await logout();
      
      toast({ 
        title: 'Account created successfully!', 
        description: 'Please check your email (including spam folder) for the verification link. You must verify your email before you can sign in.', 
        status: 'success',
        duration: 8000,
        isClosable: true
      });
      
      // Redirect to login page instead of home
      navigate('/login');
    } catch (e: any) {
      console.error('Signup error:', e);
      toast({ 
        title: 'Signup failed', 
        description: e.message || 'An error occurred during signup', 
        status: 'error',
        duration: 5000
      });
    }
  };

  const handlePhoneVerification = async () => {
    if (!phone) {
      toast({ title: 'Phone number required', status: 'error' });
      return;
    }

    try {
      setIsVerifyingPhone(true);
      const result = await sendPhoneVerification(phone);
      setConfirmationResult(result);
      onPhoneModalOpen();
      toast({ title: 'Verification code sent', description: 'Please check your phone for the verification code.', status: 'success' });
    } catch (e: any) {
      toast({ title: 'Phone verification failed', description: e.message, status: 'error' });
    } finally {
      setIsVerifyingPhone(false);
    }
  };

  const handleVerifyPhoneCode = async () => {
    if (!confirmationResult || !verificationCode) {
      toast({ title: 'Verification code required', status: 'error' });
      return;
    }

    try {
      setIsVerifyingPhone(true);
      await verifyPhoneCode(confirmationResult, verificationCode);
      
      // Create user profile with phone verification
      await signupWithPhone(phone, {
        username,
        name,
        email,
        language,
        location,
        age: age ? parseInt(age) : undefined,
        gender,
        backupEmail: backupEmail || undefined
      });

      toast({ title: 'Account created!', description: 'Your phone number has been verified.', status: 'success' });
      onPhoneModalClose();
      navigate('/');
    } catch (e: any) {
      toast({ title: 'Verification failed', description: e.message, status: 'error' });
    } finally {
      setIsVerifyingPhone(false);
    }
  };

  return (
    <Container maxW="900px" py={8}>
      <Box
        bg={cardBg}
        borderRadius="xl"
        p={{ base: 6, md: 8 }}
        boxShadow="lg"
        border="1px solid"
        borderColor={inputBorder}
      >
        <VStack gap={6} align="stretch">
          <VStack gap={2} textAlign="center">
            <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold" color={textColor}>
              Create Account
            </Text>
            <Text color={labelColor} fontSize="md">
              Join Vindie Stream and start creating
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
            <FormControl>
              <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">Username *</FormLabel>
              <Input 
                placeholder="Enter username" 
                bg={inputBg}
                border="1px solid"
                borderColor={inputBorder}
                color={textColor}
                _placeholder={{ color: labelColor }} 
                _focus={{ bg: inputFocusBg, borderColor: inputFocusBorder }} 
                value={username} 
                onChange={(e) => setUsername(e.target.value)} 
              />
            </FormControl>
            
            <FormControl>
              <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">Name *</FormLabel>
              <Input 
                placeholder="Enter your name" 
                bg={inputBg}
                border="1px solid"
                borderColor={inputBorder}
                color={textColor}
                _placeholder={{ color: labelColor }} 
                _focus={{ bg: inputFocusBg, borderColor: inputFocusBorder }} 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
              />
            </FormControl>
            
            <FormControl>
              <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">Email *</FormLabel>
              <Input 
                placeholder="Enter your email" 
                type="email" 
                bg={inputBg}
                border="1px solid"
                borderColor={inputBorder}
                color={textColor}
                _placeholder={{ color: labelColor }} 
                _focus={{ bg: inputFocusBg, borderColor: inputFocusBorder }} 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
              />
            </FormControl>
            
            <FormControl>
              <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">Password *</FormLabel>
              <InputGroup>
                <Input 
                  placeholder="Enter password" 
                  type={showPassword ? "text" : "password"} 
                  bg={inputBg}
                  border="1px solid"
                  borderColor={inputBorder}
                  color={textColor}
                  _placeholder={{ color: labelColor }} 
                  _focus={{ bg: inputFocusBg, borderColor: inputFocusBorder }} 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                />
                <InputRightElement width="3rem">
                  <IconButton
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    icon={<IconWrapper icon={showPassword ? FiEyeOff : FiEye} size={18} />}
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                    color={labelColor}
                    _hover={{ color: textColor }}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>
            
            <FormControl>
              <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">Confirm Password *</FormLabel>
              <InputGroup>
                <Input 
                  placeholder="Re-enter password" 
                  type={showConfirmPassword ? "text" : "password"} 
                  bg={inputBg}
                  border="1px solid"
                  borderColor={inputBorder}
                  color={textColor}
                  _placeholder={{ color: labelColor }} 
                  _focus={{ bg: inputFocusBg, borderColor: inputFocusBorder }} 
                  value={confirmPassword} 
                  onChange={(e) => setConfirmPassword(e.target.value)} 
                />
                <InputRightElement width="3rem">
                  <IconButton
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    icon={<IconWrapper icon={showConfirmPassword ? FiEyeOff : FiEye} size={18} />}
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    color={labelColor}
                    _hover={{ color: textColor }}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>
            
            <FormControl>
              <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">Phone Number</FormLabel>
              <Input 
                placeholder="Enter phone number" 
                type="tel" 
                bg={inputBg}
                border="1px solid"
                borderColor={inputBorder}
                color={textColor}
                _placeholder={{ color: labelColor }} 
                _focus={{ bg: inputFocusBg, borderColor: inputFocusBorder }} 
                value={phone} 
                onChange={(e) => setPhone(e.target.value)} 
              />
            </FormControl>
            
            <FormControl>
              <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">Language</FormLabel>
              <Select 
                bg={inputBg}
                border="1px solid"
                borderColor={inputBorder}
                color={textColor}
                _focus={{ bg: inputFocusBg, borderColor: inputFocusBorder }} 
                value={language} 
                onChange={(e) => setLanguage(e.target.value)}
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
                <option value="it">Italian</option>
                <option value="pt">Portuguese</option>
                <option value="ru">Russian</option>
                <option value="ja">Japanese</option>
                <option value="ko">Korean</option>
                <option value="zh">Chinese</option>
              </Select>
            </FormControl>
            
            <FormControl>
              <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">Location</FormLabel>
              <Input 
                placeholder="Enter location" 
                bg={inputBg}
                border="1px solid"
                borderColor={inputBorder}
                color={textColor}
                _placeholder={{ color: labelColor }} 
                _focus={{ bg: inputFocusBg, borderColor: inputFocusBorder }} 
                value={location} 
                onChange={(e) => setLocation(e.target.value)} 
              />
            </FormControl>
            
            <FormControl>
              <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">Age</FormLabel>
              <Input 
                placeholder="Enter age" 
                type="number" 
                bg={inputBg}
                border="1px solid"
                borderColor={inputBorder}
                color={textColor}
                _placeholder={{ color: labelColor }} 
                _focus={{ bg: inputFocusBg, borderColor: inputFocusBorder }} 
                value={age} 
                onChange={(e) => setAge(e.target.value)} 
              />
            </FormControl>
            
            <FormControl>
              <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">Gender</FormLabel>
              <Select 
                bg={inputBg}
                border="1px solid"
                borderColor={inputBorder}
                color={textColor}
                _focus={{ bg: inputFocusBg, borderColor: inputFocusBorder }} 
                value={gender} 
                onChange={(e) => setGender(e.target.value)}
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="non-binary">Non-binary</option>
                <option value="other">Other</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
              </Select>
            </FormControl>
            
            <FormControl>
              <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">Backup Email</FormLabel>
              <Input 
                placeholder="Enter backup email" 
                type="email" 
                bg={inputBg}
                border="1px solid"
                borderColor={inputBorder}
                color={textColor}
                _placeholder={{ color: labelColor }} 
                _focus={{ bg: inputFocusBg, borderColor: inputFocusBorder }} 
                value={backupEmail} 
                onChange={(e) => setBackupEmail(e.target.value)} 
              />
            </FormControl>
          </SimpleGrid>

          <FormControl>
            <Checkbox
              isChecked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              colorScheme="purple"
              size="md"
            >
              <Text fontSize="sm" color={labelColor}>
                I agree to the{' '}
                <Link href="/terms" color="purple.500" textDecoration="underline" isExternal>
                  Terms and Conditions
                </Link>
                {' '}and{' '}
                <Link href="/privacy" color="purple.500" textDecoration="underline" isExternal>
                  Privacy Policy
                </Link>
              </Text>
            </Checkbox>
          </FormControl>

          <VStack gap={3}>
            <Button 
              colorScheme="purple" 
              size="lg" 
              w="100%"
              onClick={handleEmailSignup} 
              isDisabled={!acceptedTerms}
            >
              Create Account
            </Button>
            
            <Button 
              variant="outline" 
              colorScheme="green" 
              size="lg" 
              w="100%"
              onClick={handlePhoneVerification}
              isLoading={isVerifyingPhone}
              loadingText="Sending Code"
            >
              <IconWrapper icon={FiPhone} size={20} style={{ marginRight: '8px' }} />
              Verify Phone Number
            </Button>
          </VStack>

          <HStack justify="center" gap={2} pt={2}>
            <Text color={labelColor}>Already have an account?</Text>
            <Text
              color="purple.500"
              cursor="pointer"
              fontWeight="medium"
              _hover={{ color: 'purple.600', textDecoration: 'underline' }}
              onClick={() => navigate('/login')}
            >
              Sign in
            </Text>
          </HStack>

        <Divider />

        <VStack gap={3}>
          <Button variant="outline" colorScheme="white" w="100%" onClick={handleGoogleSignup}>
            <IconWrapper icon={FiMail} size={20} style={{ marginRight: '8px' }} />
            Continue with Google
          </Button>
        </VStack>

        {/* Phone Verification Modal */}
        <Modal isOpen={isPhoneModalOpen} onClose={onPhoneModalClose}>
          <ModalOverlay />
          <ModalContent bg="gray.800" color="white">
            <ModalHeader>Verify Phone Number</ModalHeader>
            <ModalBody>
              <VStack gap={4}>
                <Text color="gray.300">Enter the verification code sent to {phone}</Text>
                <Input 
                  placeholder="Verification Code" 
                  bg="gray.700" 
                  border="none" 
                  color="white" 
                  _placeholder={{ color: 'gray.400' }} 
                  _focus={{ bg: 'gray.600' }} 
                  value={verificationCode} 
                  onChange={(e) => setVerificationCode(e.target.value)} 
                />
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onPhoneModalClose}>
                Cancel
              </Button>
              <Button 
                colorScheme="green" 
                onClick={handleVerifyPhoneCode}
                isLoading={isVerifyingPhone}
                loadingText="Verifying"
              >
                Verify
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

          {/* reCAPTCHA Container */}
          <div id="recaptcha-container"></div>
        </VStack>
      </Box>
    </Container>
  );
};

export default Signup;
